import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { icon } from '../shared/components/icons';

// Statement Review — VRPlatform owner-statement PDFs verified BEFORE send.
//
// Drop a month of PDFs → POST /api/statements/upload runs pdftotext + the
// deterministic rule set (spec §5) against the per-property rate sheet, and
// every statement lands as HOLD (issuer fix) / CONFIRM (small verify) /
// APPROVE (clean). This page is the reviewer's desk: headline counts for the
// selected batch, a sortable queue, per-statement findings with the math,
// approve-overrides with a note, a Slack-ready report, and the rate table.
//
// Honest numbers (PRINCIPLES.md): every count says which batch/month it is
// from and when it was uploaded; unparsed values say "not parsed", never $0.

type FindingReview = {
  status?: 'resolved' | 'dismissed';
  status_by?: string;
  status_at?: string;
  comments?: { by: string; at: string; text: string }[];
  assignee?: { id?: string | null; name: string; at: string; by: string };   // R86
};
type Finding = { id?: string; severity: 'HIGH' | 'MED' | 'LOW'; rule_code: string; message: string; review?: FindingReview };

export class StatementsPage {
  private container: HTMLElement;
  private batches: any[] = [];           // summaries from GET /batches
  private batch: any = null;             // full batch from GET /batch/:id
  private rates: any[] = [];
  private view: 'review' | 'rates' = 'review';
  private sortKey = 'status';
  private sortDir: 1 | -1 = 1;
  private expanded: Set<string> = new Set();
  private mathOpen: Set<string> = new Set();
  private ownerEmails: Record<string, string> = {};   // R57: drafted owner notes by property
  private ownerEmailBusy: string | null = null;
  private ratesNote = '';
  // R114 fix (data-safety #0): the rate table is a full-replace editor of the
  // shared sr_rates store — remember the version it was drawn from (sent back
  // as base_version; the server refuses a stale/blank base) and never offer a
  // Save over a table that failed to load.
  private ratesVersion = '';
  private ratesLoadError = '';
  // R114 fix2: one rate-table save at a time — a double-click's second PUT
  // carried the same (now stale) base and its 409 overwrote "Saved" with
  // "Save failed" although the first save landed.
  private ratesSaving = false;
  private loaded = false;
  // R85 (Jesse: comment + approve each finding): expanded threads, unsent
  // drafts (draw() rebuilds the whole body — text must survive), in-flight key.
  private frOpen: Set<string> = new Set();          // `${property}\u0000${finding_id}`
  private frDrafts: Map<string, string> = new Map();
  private frBusy: string | null = null;
  // R86 (Jesse: "assign these flags to a specific person... then them be able
  // to filter for those that they are assigned to").
  private reviewers: { id: string; name: string; role: string }[] = [];
  private assigneeFilter = '';                      // '' everyone · 'me' · reviewer id
  // R88 #1 (Michelle): the worklist FREEZES while she works — resolving a
  // finding must never re-sort the row away (status sort flips it to APPROVE)
  // or drop the statement mid-review (the assignee filter losing its last
  // open match). Order + membership snapshot on batch load / sort click /
  // filter change; the data INSIDE frozen rows stays live. propSearch is her
  // explicit act, so it filters live and never freezes.
  private rowSnapshot: string[] | null = null;      // property keys, frozen order + membership
  private propSearch = '';
  private ovNotes: Map<string, string> = new Map();   // review #2: un-submitted override notes must survive redraws (live search redraws per keystroke)

  constructor(container: HTMLElement) {
    this.container = container;
  }

  // ── helpers ─────────────────────────────────────────────────
  private fmt2(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    const sign = n < -0.004 ? '-' : '';
    return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private byWho(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || 'office'); } catch { return 'office'; }
  }
  private findingCleared(x: Finding): boolean {
    return !!(x.review && (x.review.status === 'resolved' || x.review.status === 'dismissed'));
  }
  private me(): { id: string; name: string } {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return { id: String(p.id || ''), name: String(p.full_name || p.email || '') }; } catch { return { id: '', name: '' }; }
  }
  // Does this finding match the active assignee filter? (id first, name as the
  // fallback for assignments made before ids existed or by-name matches)
  private matchesAssignee(x: Finding, filter = this.assigneeFilter): boolean {
    if (!filter) return true;
    const a = x.review && x.review.assignee;
    if (!a) return false;
    // Review #4: the name fallback exists ONLY for id-less legacy assignments —
    // an explicit non-matching id must never be rescued by a same-name match.
    if (filter === 'me') { const m = this.me(); return a.id ? a.id === m.id : !!(m.name && a.name === m.name); }
    const r = this.reviewers.find(v => v.id === filter);
    return a.id ? a.id === filter : !!(r && a.name === r.name);
  }
  // Open findings assigned per filter value — powers the chip counts.
  private assignedOpenCount(filter: string): number {
    let n = 0;
    for (const st of (this.batch?.statements || [])) {
      for (const f of (st.findings || []) as Finding[]) if (!this.findingCleared(f) && this.matchesAssignee(f, filter)) n++;
    }
    return n;
  }
  private uploadedLabel(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  private statusChip(s: any): string {
    const cls = s.status === 'HOLD' ? 'pill-red' : s.status === 'CONFIRM' ? 'pill-amber' : 'pill-green';
    const over = s.status_overridden ? ` title="Overridden${s.override_note ? `: ${this.esc(s.override_note)}` : ''}"` : '';
    return `<span class="pill ${cls}${s.status_overridden ? ' sr-chip-over' : ''}"${over}>${this.esc(s.status)}${s.status_overridden ? ' ✎' : ''}</span>${s.no_bookings ? ' <span class="pill pill-gray">no bookings</span>' : ''}`;
  }

  // ── data ────────────────────────────────────────────────────
  private async loadBatches(selectId?: string): Promise<void> {
    this.rowSnapshot = null;   // R88 #1: fresh data = fresh worklist snapshot
    this.batches = await fetch('/api/statements/batches').then(r => r.ok ? r.json() : []).catch(() => []);
    const id = selectId || this.batch?.id || this.batches[0]?.id;
    if (id) {
      this.batch = await fetch(`/api/statements/batch/${id}`).then(r => r.ok ? r.json() : null).catch(() => null);
    } else {
      this.batch = null;
    }
  }
  private async loadRates(): Promise<void> {
    const r = await fetch('/api/statements/rates').catch(() => null);
    const j = r ? await r.json().catch(() => null) : null;
    const ok = !!r && r.ok && Array.isArray(j);
    this.rates = ok ? j : [];
    this.ratesVersion = ok ? (r!.headers.get('X-Settings-Version') || '') : '';
    this.ratesLoadError = ok ? '' : String((j && (j as any).error) || "Couldn't load the rate sheet just now — nothing was changed. Reload the page in a moment.");
  }
  private async loadReviewers(): Promise<void> {
    const j = await fetch('/api/statements/reviewers').then(r => r.ok ? r.json() : null).catch(() => null);
    if (j && Array.isArray(j.reviewers)) this.reviewers = j.reviewers;
  }

  // ── render skeleton ─────────────────────────────────────────
  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <style>
          .sr-head { display:flex; align-items:baseline; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
          .sr-head h2 { margin:0; font-size:17px; font-weight:600; letter-spacing:-0.02em; color:var(--text); }
          .sr-head .sub { font-size:12px; color:var(--text-muted); }
          .sr-tabs { margin-left:auto; display:flex; gap:6px; }
          .sr-tab { padding:6px 15px; border:1px solid var(--border); border-radius:var(--radius-pill); background:var(--card); color:var(--text-muted); font-size:12px; font-weight:500; cursor:pointer; transition:background .15s ease, color .15s ease, border-color .15s ease; }
          .sr-tab:hover { background:var(--bg-subtle); color:var(--text); }
          .sr-tab.active { background:var(--text); color:#fff; border-color:var(--text); }
          .sr-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:16px 18px; margin-bottom:14px; }
          .sr-upload-row { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
          .sr-upload-row input[type=file] { font-size:12px; color:var(--text-muted); }
          .sr-btn { padding:8px 16px; border:1px solid var(--text); border-radius:var(--radius-sm); background:var(--text); color:#fff; font-size:12px; font-weight:600; cursor:pointer; transition:opacity .15s ease, background .15s ease; }
          .sr-btn:hover:not(:disabled) { opacity:0.88; }
          .sr-btn:disabled { opacity:0.4; cursor:default; }
          .sr-btn-ghost { background:var(--card); color:var(--text); border-color:var(--border); }
          .sr-btn-ghost:hover:not(:disabled) { background:var(--bg-subtle); opacity:1; }
          .sr-note { font-size:12px; color:var(--text-muted); }
          .sr-kpis { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:12px; margin-bottom:14px; }
          .sr-kpi { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:14px 16px; }
          .sr-kpi-label { font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
          .sr-kpi-value { font-size:25px; font-weight:600; margin-top:5px; line-height:1.1; color:var(--text); font-variant-numeric:tabular-nums; letter-spacing:-0.02em; }
          .sr-kpi-sub { font-size:11px; color:var(--text-faint); margin-top:4px; }
          .sr-chip { display:inline-block; font-size:10px; font-weight:600; padding:3px 10px; border-radius:var(--radius-pill); white-space:nowrap; letter-spacing:0.02em; }
          .sr-chip-hold { background:var(--danger-soft); color:var(--danger-text); }
          .sr-chip-confirm { background:var(--warning-soft); color:var(--warning-text); }
          .sr-chip-approve { background:var(--success-soft); color:var(--success-text); }
          .sr-chip-nb { background:var(--bg-subtle); color:var(--text-muted); font-weight:500; }
          .sr-chip-over { outline:1.5px dashed currentColor; outline-offset:1px; }
          table.sr-table { width:100%; border-collapse:collapse; background:var(--card); }
          table.sr-table th { font-size:10.5px; font-weight:600; color:var(--text-muted); text-align:left; padding:10px 12px; border-bottom:1px solid var(--border); cursor:pointer; user-select:none; white-space:nowrap; text-transform:uppercase; letter-spacing:0.05em; transition:color .12s ease; }
          table.sr-table th:hover { color:var(--text); }
          table.sr-table td { font-size:12.5px; padding:11px 12px; border-bottom:1px solid var(--border-soft); vertical-align:top; color:var(--text); }
          table.sr-table tr.sr-row { cursor:pointer; transition:background .12s ease; }
          table.sr-table tr.sr-row:hover { background:var(--bg-subtle); }
          td.r, th.r { text-align:right !important; }
          td.num, td.mono, .num, .mono { font-variant-numeric:tabular-nums; }
          .sr-detail { background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:var(--radius-sm); padding:14px 16px; }
          .sr-sev { font-size:10px; font-weight:700; padding:2px 8px; border-radius:var(--radius-sm); margin-right:8px; letter-spacing:0.03em; }
          .sr-sev-HIGH { background:var(--danger-soft); color:var(--danger-text); }
          .sr-sev-MED { background:var(--warning-soft); color:var(--warning-text); }
          .sr-sev-LOW { background:var(--bg-subtle); color:var(--text-muted); }
          .sr-finding { font-size:12px; margin:6px 0; line-height:1.5; color:var(--text); }
          .sr-fr-chip { font-size:10px; font-weight:700; border-radius:999px; padding:1px 8px; margin-left:6px; white-space:nowrap; }
          .sr-frf-chip { font-size:11.5px; padding:4px 12px; border:1.5px solid var(--border); border-radius:999px; background:var(--card,#fff); color:var(--text-muted); cursor:pointer; transition:all .12s ease; }
          .sr-frf-chip:hover { border-color:var(--border-strong,#cbd5e1); color:var(--text); }
          .sr-frf-chip.on { background:var(--text,#0f172a); border-color:var(--text,#0f172a); color:#fff; font-weight:600; }
          .sr-fr-assignee { font-size:10px; font-weight:600; border-radius:999px; padding:1px 8px; margin-left:6px; white-space:nowrap; background:var(--accent-soft,#eaf2ff); color:var(--accent-hover,#1d4ed8); }
          .sr-fr-thread { margin:6px 0 4px 14px; padding:8px 10px; border-left:2px solid var(--border); display:flex; flex-direction:column; gap:5px; background:var(--bg-subtle,#f8fafc); border-radius:0 8px 8px 0; }
          .sr-fr-comment { font-size:12px; line-height:1.45; }
          .sr-fr-comment div { white-space:pre-wrap; word-break:break-word; }
          .sr-fr-input { flex:1; min-width:220px; font-size:12px; padding:5px 9px; border:1px solid var(--border); border-radius:7px; font-family:inherit; background:var(--card,#fff); }
          .sr-math { font-family:var(--font-mono, monospace); font-variant-numeric:tabular-nums; font-size:11.5px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:12px 14px; margin-top:8px; white-space:pre-wrap; line-height:1.55; color:var(--text); }
          .sr-link { font-size:11.5px; color:var(--text-muted); text-decoration:underline; text-underline-offset:2px; cursor:pointer; background:none; border:none; padding:0; transition:color .12s ease; }
          .sr-link:hover { color:var(--text); }
          .sr-callout { border:1px solid var(--danger-soft); background:var(--danger-soft); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:14px 16px; margin-bottom:14px; }
          .sr-callout h4 { margin:0 0 8px; font-size:13px; font-weight:600; color:var(--danger-text); }
          .sr-callout div { font-size:12px; line-height:1.6; color:var(--text); }
          .sr-rates-wrap { overflow-x:auto; }
          table.sr-rates th { position:sticky; top:0; background:var(--card); cursor:default; z-index:1; }
          table.sr-rates th:hover { color:var(--text-muted); }
          table.sr-rates input[type=text], table.sr-rates input[type=number] { width:100%; min-width:52px; padding:5px 7px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:11.5px; background:var(--card); color:var(--text); transition:border-color .12s ease; }
          table.sr-rates input[type=text]:focus, table.sr-rates input[type=number]:focus { outline:none; border-color:var(--text-muted); }
          table.sr-rates input.wide { min-width:130px; }
          table.sr-rates select { padding:5px 6px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:11.5px; background:var(--card); color:var(--text); }
          .sr-override-box { display:flex; gap:8px; align-items:center; margin-top:12px; flex-wrap:wrap; }
          .sr-override-box input { padding:7px 11px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:12px; min-width:260px; background:var(--card); color:var(--text); transition:border-color .12s ease; }
          .sr-override-box input:focus { outline:none; border-color:var(--text-muted); }
          .sr-empty { text-align:center; padding:48px 24px; color:var(--text-faint); font-size:13px; line-height:1.7; }
        </style>
        <div style="display:flex; align-items:flex-end; gap:12px; flex-wrap:wrap;">
          <div class="dash-head" style="margin-bottom:0;">
            <h1>Statement Review</h1>
            <p>VRPlatform owner statements, verified against the rate sheet before they go out</p>
          </div>
          <div class="sr-tabs" style="margin-left:auto; margin-bottom:4px;">
            <button class="sr-tab ${this.view === 'review' ? 'active' : ''}" data-view="review">Review</button>
            <button class="sr-tab ${this.view === 'rates' ? 'active' : ''}" data-view="rates">Rate table</button>
          </div>
        </div>
        <div class="sr-card" id="sr-upload">
          <div class="sr-upload-row">
            <input type="file" id="sr-files" multiple accept=".pdf" />
            <span class="sr-note" id="sr-file-note">No files chosen — expects Property_MM_YYYY.pdf, one statement per property per month</span>
            <button class="sr-btn" id="sr-run" disabled>Run review</button>
          </div>
          <div class="sr-note" id="sr-progress" style="margin-top:6px; display:none;"></div>
        </div>
        <div id="sr-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;

    this.bindStatic();
    await Promise.all([this.loadBatches(), this.loadRates(), this.loadReviewers()]);
    this.loaded = true;
    this.draw();
  }

  private bindStatic(): void {
    this.container.querySelectorAll('.sr-tab').forEach(b => b.addEventListener('click', () => {
      this.view = (b as HTMLElement).dataset.view as any;
      this.container.querySelectorAll('.sr-tab').forEach(x => x.classList.toggle('active', x === b));
      this.draw();
    }));
    const input = document.getElementById('sr-files') as HTMLInputElement;
    const note = document.getElementById('sr-file-note')!;
    const run = document.getElementById('sr-run') as HTMLButtonElement;
    input?.addEventListener('change', () => {
      const files = Array.from(input.files || []);
      run.disabled = files.length === 0;
      if (files.length === 0) { note.textContent = 'No files chosen'; return; }
      // month label from filenames {Property}_MM_YYYY.pdf
      const months = new Set<string>();
      for (const f of files) {
        const m = f.name.match(/_(\d{2})_(\d{4})\.pdf$/i);
        if (m) months.add(new Date(+m[2], +m[1] - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
      }
      const monthLabel = months.size === 0 ? 'month not detected from filenames' : [...months].join(' + ') + (months.size > 1 ? ' — mixed months in one batch' : '');
      note.textContent = `${files.length} PDF${files.length === 1 ? '' : 's'} · ${monthLabel}`;
    });
    run?.addEventListener('click', () => this.upload());
  }

  private async upload(): Promise<void> {
    const input = document.getElementById('sr-files') as HTMLInputElement;
    const run = document.getElementById('sr-run') as HTMLButtonElement;
    const prog = document.getElementById('sr-progress')!;
    const files = Array.from(input?.files || []);
    if (files.length === 0) return;
    run.disabled = true;
    prog.style.display = 'block';
    prog.textContent = `Parsing ${files.length} PDF${files.length === 1 ? '' : 's'} and running the rule set…`;
    try {
      const fd = new FormData();
      for (const f of files) fd.append('files', f, f.name);
      const res = await fetch('/api/statements/upload', { method: 'POST', body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      prog.textContent = `Reviewed ${body.statements.length} statement${body.statements.length === 1 ? '' : 's'} for ${body.month}.`;
      // R100 C5: each PDF is filed into the matched owner's documents (owner
      // line on the statement, else the property's assigned owner).
      try {
        const fr = await fetch('/api/owners/file-statements', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ batch_id: body.id }) });
        const fj = await fr.json().catch(() => ({} as any));
        if (fr.ok) prog.textContent += ` Filed ${(fj.filed || []).length} PDF${(fj.filed || []).length === 1 ? '' : 's'} to owner profiles${fj.already ? ` (${fj.already} already there)` : ''}${(fj.unmatched || []).length ? ` · ${fj.unmatched.length} not matched to an owner: ${fj.unmatched.slice(0, 6).map((u: any) => u.property).join(', ')}${fj.unmatched.length > 6 ? '…' : ''}` : ''}${fj.no_pdf ? ` · ${fj.no_pdf} without a stored PDF` : ''}.`;
        else prog.textContent += ` (Owner filing skipped — ${fj.error || `HTTP ${fr.status}`}.)`;
      } catch (_) { /* filing is a convenience; the review stands */ }
      input.value = '';
      this.expanded.clear(); this.mathOpen.clear();
      this.view = 'review';
      await this.loadBatches(body.id);
      this.container.querySelectorAll('.sr-tab').forEach(x => x.classList.toggle('active', (x as HTMLElement).dataset.view === 'review'));
      this.draw();
    } catch (e: any) {
      prog.textContent = `Upload failed: ${e.message}`;
      run.disabled = false;
    }
  }

  // ── main draw ───────────────────────────────────────────────
  private draw(): void {
    const body = document.getElementById('sr-body');
    const uploadCard = document.getElementById('sr-upload');
    if (!body) return;
    if (uploadCard) uploadCard.style.display = this.view === 'review' ? '' : 'none';
    if (!this.loaded) return;
    // R88 review #1: EVERY draw — including the async completions of frSend /
    // resolve / override / owner-email — must preserve search focus+caret,
    // not just draws the search input itself triggered.
    const ae = document.activeElement as HTMLInputElement | null;
    const wasSearch = !!ae && ae.id === 'sr-prop-search';
    const pos = wasSearch ? (ae!.selectionStart ?? ae!.value.length) : 0;
    body.innerHTML = this.view === 'rates' ? this.drawRates() : this.drawReview();
    this.bindBody();
    if (wasSearch) {
      const el = document.getElementById('sr-prop-search') as HTMLInputElement | null;
      if (el) { el.focus(); try { el.setSelectionRange(pos, pos); } catch { /* type=search may refuse */ } }
    }
  }

  // ── review view ─────────────────────────────────────────────
  private sortedStatements(): any[] {
    const sts = [...(this.batch?.statements || [])];
    const ord: Record<string, number> = { HOLD: 0, CONFIRM: 1, APPROVE: 2 };
    const val = (s: any): any => {
      switch (this.sortKey) {
        case 'property': return String(s.property || '').toLowerCase();
        case 'owner': return String(s.owner || '').toLowerCase();
        case 'status': return ord[s.status] ?? 9;
        case 'net_income': return s.net_income ?? -Infinity;
        case 'findings': return (s.findings || []).length;
        default: return 0;
      }
    };
    sts.sort((a, b) => { const x = val(a), y = val(b); return (x < y ? -1 : x > y ? 1 : 0) * this.sortDir; });
    return sts;
  }

  // R88 #1.2/#1.3: rows in FROZEN order + membership; live data inside.
  private workRows(): any[] {
    const all: any[] = this.batch?.statements || [];
    const passesFilter = (s: any) => !this.assigneeFilter
      || ((s.findings || []) as Finding[]).some(f => !this.findingCleared(f) && this.matchesAssignee(f));
    if (!this.rowSnapshot) {
      this.rowSnapshot = this.sortedStatements().filter(passesFilter).map(s => String(s.property));
    }
    const byProp = new Map(all.map((s: any) => [String(s.property), s]));
    const rows: any[] = this.rowSnapshot.map(p => byProp.get(p)).filter(Boolean);
    const inSnap = new Set(this.rowSnapshot);
    // statements that appeared after the snapshot (e.g. a re-upload) append —
    // NEW rows respect the filter; frozen rows never vanish mid-review.
    for (const s of all) if (!inSnap.has(String(s.property)) && passesFilter(s)) rows.push(s);
    const q = this.propSearch.trim().toLowerCase();
    return q ? rows.filter(s => String(s.property || '').toLowerCase().includes(q) || String(s.owner || '').toLowerCase().includes(q)) : rows;
  }

  private drawReview(): string {
    if (!this.batch) {
      return `<div class="sr-card sr-empty">
        No review batches yet — nothing has been uploaded.<br/>
        Drop a month of VRPlatform owner-statement PDFs above (one per property, named <span class="mono">Property_MM_YYYY.pdf</span>)
        and every statement gets a HOLD / CONFIRM / APPROVE verdict from the deterministic checks.<br/>
        The server keeps the last 6 batches.</div>`;
    }
    const b = this.batch;
    const sts: any[] = b.statements || [];
    const hold = sts.filter(s => s.status === 'HOLD').length;
    const confirm = sts.filter(s => s.status === 'CONFIRM').length;
    const approve = sts.filter(s => s.status === 'APPROVE').length;
    const nb = sts.filter(s => s.no_bookings).length;
    const negatives = sts.filter(s => (s.ending_balance ?? 0) < -0.005).sort((a, c) => (a.ending_balance ?? 0) - (c.ending_balance ?? 0));
    const picker = this.batches.length > 1 ? `
      <select id="sr-batch-pick" style="padding:6px 10px; border:1px solid var(--border); border-radius:7px; font-size:12px;">
        ${this.batches.map(x => `<option value="${this.esc(x.id)}" ${x.id === b.id ? 'selected' : ''}>${this.esc(x.month)} — uploaded ${this.esc(this.uploadedLabel(x.uploaded_at))} (${x.counts?.total ?? '?'} statements)</option>`).join('')}
      </select>` : '';
    const arrow = (k: string) => this.sortKey === k ? (this.sortDir === 1 ? ' ▲' : ' ▼') : '';
    const kpi = (label: string, ico: string, value: string | number, sub: string, tone = '') => `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value num">${value}</div>
        <div class="kpi-card-cap">${sub}</div>
      </div>`;
    return `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:12px; flex-wrap:wrap;">
        <div style="font-size:13px; font-weight:600;">${this.esc(b.month)} batch</div>
        <div class="sr-note">uploaded ${this.esc(this.uploadedLabel(b.uploaded_at))} · ${sts.length} statements · last ${this.batches.length} of 6 kept batches shown in picker</div>
        ${picker}
        <button class="sr-btn sr-btn-ghost" id="sr-copy-slack" style="margin-left:auto;">Copy Slack report</button>
      </div>
      <div class="kpi-grid">
        ${kpi('Statements', 'receipt', sts.length, `in the ${this.esc(b.month)} batch`)}
        ${kpi('Hold', 'flag', hold, `of ${sts.length} — issuer must fix`, hold ? 'tone-bad' : '')}
        ${kpi('Confirm', 'search', confirm, `of ${sts.length} — small human verify`)}
        ${kpi('Approve', 'sparkles', approve, `of ${sts.length} — clean per the rules`, approve && !hold ? 'tone-good' : '')}
        ${kpi('No bookings', 'calendar', nb, `of ${sts.length} — summary box still checked`)}
      </div>
      ${negatives.length ? `<div class="sr-callout">
        <h4 style="display:flex; align-items:center; gap:7px;">${icon('wallet', 15)} ${negatives.length} negative ending balance${negatives.length === 1 ? '' : 's'} in this batch</h4>
        ${negatives.map(s => `<div>• <strong>${this.esc(s.property)}</strong> — ending balance <span class="num mono">${this.fmt2(s.ending_balance)}</span></div>`).join('')}
      </div>` : ''}
      <div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin:14px 0 10px;">
        <input type="search" id="sr-prop-search" value="${this.esc(this.propSearch)}" placeholder="🔎 property or owner…" style="padding:6px 11px; border:1px solid var(--border); border-radius:999px; font-size:12px; width:190px; background:var(--card,#fff);">
        <span class="sr-note" style="text-transform:uppercase; letter-spacing:.05em; font-weight:600;">Assigned to</span>
        <button class="sr-frf-chip ${this.assigneeFilter === '' ? 'on' : ''}" data-fr-filter="">Everyone</button>
        <button class="sr-frf-chip ${this.assigneeFilter === 'me' ? 'on' : ''}" data-fr-filter="me">Me${this.assignedOpenCount('me') ? ` <span class="num">· ${this.assignedOpenCount('me')}</span>` : ''}</button>
        <select id="sr-frf-person" style="padding:5px 9px; border:1px solid var(--border); border-radius:999px; font-size:11.5px; background:var(--card,#fff); ${this.assigneeFilter && this.assigneeFilter !== 'me' ? 'border-color:var(--text); font-weight:600;' : ''}">
          <option value="">person…</option>
          ${this.reviewers.map(r => `<option value="${this.esc(r.id)}" ${this.assigneeFilter === r.id ? 'selected' : ''}>${this.esc(r.name)}${this.assignedOpenCount(r.id) ? ` (${this.assignedOpenCount(r.id)})` : ''}</option>`).join('')}
        </select>
        ${this.assigneeFilter ? `<span class="sr-note">showing statements with open flags assigned ${this.assigneeFilter === 'me' ? 'to you' : `to ${this.esc((this.reviewers.find(r => r.id === this.assigneeFilter) || { name: 'them' }).name)}`}</span>` : ''}
      </div>
      <div class="sr-card" style="padding:0; overflow:hidden;">
        <table class="sr-table">
          <thead><tr>
            <th data-sort="property">Property${arrow('property')}</th>
            <th data-sort="owner">Owner${arrow('owner')}</th>
            <th data-sort="status">Status${arrow('status')}</th>
            <th class="r" data-sort="net_income">Net income${arrow('net_income')}</th>
            <th class="r" data-sort="findings">Findings${arrow('findings')}</th>
            <th>Override</th>
          </tr></thead>
          <tbody>
            ${(() => {
              const rows = this.workRows();
              const empty = this.propSearch.trim()
                ? `No statements match “${this.esc(this.propSearch.trim())}”.`
                : `No statements with open flags assigned ${this.assigneeFilter === 'me' ? 'to you' : 'to them'} in this batch. 🎉`;
              return rows.map(s2 => this.rowHtml(s2)).join('') || `<tr><td colspan="6" style="text-align:center; color:var(--text-muted); padding:24px;">${empty}</td></tr>`;
            })()}
          </tbody>
        </table>
      </div>`;
  }

  private rowHtml(s: any): string {
    const key = String(s.property);
    const f: Finding[] = s.findings || [];
    const openF = f.filter(x => !this.findingCleared(x));   // R85: cleared findings stop counting
    const cleared = f.length - openF.length;
    const counts = ['HIGH', 'MED', 'LOW'].map(sev => [sev, openF.filter(x => x.severity === sev).length] as const).filter(([, n]) => n > 0);
    const findingsCell = f.length === 0 ? '<span class="sr-note">0 — clean</span>'
      : `${counts.map(([sev, n]) => `<span class="sr-sev sr-sev-${sev}">${n} ${sev}</span>`).join(' ')}${!counts.length ? '<span class="sr-note" style="color:var(--success-text,#065f46);">✓ all cleared</span>' : ''}${cleared ? ` <span class="sr-note num">· ${cleared} cleared</span>` : ''}`;
    const main = `
      <tr class="sr-row" data-prop="${this.esc(key)}">
        <td style="font-weight:600;">${this.esc(s.property)}</td>
        <td>${s.owner ? this.esc(s.owner) : '<span class="sr-note">not parsed</span>'}</td>
        <td>${this.statusChip(s)}</td>
        <td class="r num mono">${s.net_income == null ? '<span class="sr-note" title="Summary box did not parse">not parsed</span>' : this.fmt2(s.net_income)}</td>
        <td class="r">${findingsCell}</td>
        <td>${s.status_overridden ? `<span class="sr-note">✎ ${this.esc(s.override_note || 'overridden')}</span>` : s.status !== 'APPROVE' ? `<button class="sr-link" data-override-open="${this.esc(key)}">override…</button>` : ''}</td>
      </tr>`;
    if (!this.expanded.has(key)) return main;
    return main + `<tr><td colspan="6"><div class="sr-detail">${this.detailHtml(s)}</div></td></tr>`;
  }

  private detailHtml(s: any): string {
    const key = String(s.property);
    const f: Finding[] = s.findings || [];
    // R85: each finding is now a workable item — comment thread + resolve
    // ("handled") / dismiss ("not actually an issue"). Cleared findings stop
    // driving the verdict; the server recomputes HOLD/CONFIRM/APPROVE.
    const findingRow = (x: Finding, sev: string) => {
      const fid = x.id || '';
      // Review #5: a finding with no id (old server behind a staggered deploy)
      // gets the read-only pre-R85 line — actionable controls on an empty id
      // would collide every thread onto one key and 404 on the server.
      if (!fid) return `<div class="sr-finding"><span class="sr-sev sr-sev-${sev}">${sev}</span><span class="mono" style="color:var(--text-muted); margin-right:6px;">${this.esc(x.rule_code)}</span>${this.esc(x.message)}</div>`;
      // Review #4 (HIGH): the key must NEVER ride an HTML attribute — the
      // parser rewrites U+0000 to U+FFFD, so dataset reads never equal the
      // JS-built key and no thread would ever open. Attributes carry the two
      // parts separately; handlers rebuild the key.
      const fkey = `${key}\u0000${fid}`;
      const rv = x.review || {};
      const comments = rv.comments || [];
      const cleared = this.findingCleared(x);
      const busy = this.frBusy === fkey;
      const open = this.frOpen.has(fkey);
      const statusChip = rv.status === 'resolved'
        ? `<span class="sr-fr-chip" style="color:var(--success-text,#065f46); background:var(--success-soft,#ecfdf5);">✓ resolved${rv.status_by ? ` · ${this.esc(rv.status_by)}` : ''}</span>`
        : rv.status === 'dismissed'
          ? `<span class="sr-fr-chip" style="color:var(--text-muted); background:var(--bg-subtle,#f8fafc);">✕ dismissed${rv.status_by ? ` · ${this.esc(rv.status_by)}` : ''}</span>`
          : '';
      const thread = open ? `
        <div class="sr-fr-thread">
          ${comments.map(c => `<div class="sr-fr-comment"><strong>${this.esc(c.by)}</strong> <span class="sr-note">${this.esc(this.uploadedLabel(c.at))}</span><div>${this.esc(c.text)}</div></div>`).join('')}
          <div style="display:flex; gap:6px; flex-wrap:wrap; align-items:center; margin-top:${comments.length ? '6px' : '0'};">
            <input type="text" class="sr-fr-input" data-fr-input data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" value="${this.esc(this.frDrafts.get(fkey) || '')}" placeholder="Comment — what you checked, why it's fine, what was fixed…" maxlength="500" ${busy ? 'disabled' : ''}>
            <button class="sr-btn sr-btn-ghost" data-fr-act="comment" data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" ${busy ? 'disabled' : ''}>💬 Comment</button>
            ${cleared
              ? `<button class="sr-btn sr-btn-ghost" data-fr-act="reopen" data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" ${busy ? 'disabled' : ''}>↩ Reopen</button>`
              : `<button class="sr-btn sr-btn-ghost" style="color:var(--success-text,#065f46);" data-fr-act="resolve" data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" ${busy ? 'disabled' : ''} title="The issue was real and it's handled">✓ Resolve</button>
              <button class="sr-btn sr-btn-ghost" data-fr-act="dismiss" data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" ${busy ? 'disabled' : ''} title="Not actually an issue — fine to send as-is">✕ Dismiss</button>`}
            <select data-fr-assign data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" ${busy ? 'disabled' : ''} title="Assign this flag to a teammate — they filter for their own up top" style="font-size:11px; padding:4px 8px; border:1px solid var(--border); border-radius:7px; background:var(--card,#fff); max-width:170px;">
              <option value="">${rv.assignee ? 'reassign…' : '→ assign…'}</option>
              ${this.reviewers.map(r2 => `<option value="${this.esc(r2.id)}" ${rv.assignee && rv.assignee.id === r2.id ? 'selected' : ''}>${this.esc(r2.name)}</option>`).join('')}
            </select>
            ${rv.assignee ? `<button class="sr-btn sr-btn-ghost" data-fr-act="unassign" data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" ${busy ? 'disabled' : ''} title="Clear the assignment">✕ unassign</button>` : ''}
            ${busy ? '<span class="sr-note">saving…</span>' : ''}
          </div>
        </div>` : '';
      return `
        <div class="sr-finding" style="${cleared ? 'opacity:.62;' : ''}">
          <span class="sr-sev sr-sev-${sev}">${sev}</span><span class="mono" style="color:var(--text-muted); margin-right:6px;">${this.esc(x.rule_code)}</span><span style="${rv.status === 'dismissed' ? 'text-decoration:line-through;' : ''}">${this.esc(x.message)}</span>
          ${statusChip}
          ${rv.assignee ? `<span class="sr-fr-assignee" title="Assigned by ${this.esc(rv.assignee.by || '')}${rv.assignee.at ? ` · ${this.esc(this.uploadedLabel(rv.assignee.at))}` : ''}">👤 ${this.esc(rv.assignee.name)}</span>` : ''}
          <button class="sr-link" data-fr-toggle data-fr-prop="${this.esc(key)}" data-fr-id="${this.esc(fid)}" style="margin-left:6px;">${open ? '▾' : '💬'} ${comments.length || ''}${open ? ' close' : comments.length ? '' : ' review…'}</button>
          ${thread}
        </div>`;
    };
    const groups = (['HIGH', 'MED', 'LOW'] as const).map(sev => {
      const list = f.filter(x => x.severity === sev);
      if (!list.length) return '';
      return `<div style="margin-bottom:6px;">${list.map(x => findingRow(x, sev)).join('')}</div>`;
    }).join('');
    const overrideBox = s.status_overridden
      ? `<div class="sr-note" style="margin-top:10px;">Overridden to ${this.esc(s.status)} ${s.override_at ? `on ${this.esc(this.uploadedLabel(s.override_at))}` : ''}${s.override_note ? ` — “${this.esc(s.override_note)}”` : ''}</div>`
      : `<div class="sr-override-box">
          <input type="text" id="sr-ov-note-${this.esc(key)}" data-ov-key="${this.esc(key)}" value="${this.esc(this.ovNotes.get(key) || '')}" placeholder="Override note (why this is fine to send)" />
          <button class="sr-btn sr-btn-ghost" data-override="${this.esc(key)}">Override → APPROVE</button>
        </div>`;
    // R57: copy-paste-ready owner note explaining the month's big expenses —
    // AI drafts it, a HUMAN reads, edits, and sends it (never auto-sent).
    const emailPanel = `
      <div style="margin-top:8px;">
        <button class="sr-link" data-owner-email="${this.esc(key)}">${this.ownerEmailBusy === key ? 'Drafting…' : '✉️ Draft owner note'}</button>
        ${this.ownerEmails[key] ? `
          <div style="margin-top:6px;">
            <textarea id="sr-oe-${this.esc(key)}" data-oe-key="${this.esc(key)}" rows="7" style="width:100%; box-sizing:border-box; font-size:12.5px; padding:8px 10px; border:1px solid var(--border); border-radius:8px; font-family:inherit;">${this.esc(this.ownerEmails[key])}</textarea>
            <button class="sr-btn sr-btn-ghost" data-oe-copy="${this.esc(key)}" style="margin-top:4px;">📋 Copy to clipboard</button>
            <span class="sr-note">review + edit before sending — nothing is emailed automatically</span>
          </div>` : ''}
      </div>`;
    return `
      ${f.length === 0 ? '<div class="sr-note">No findings — every check passed.</div>' : groups}
      <button class="sr-link" data-math="${this.esc(key)}">${this.mathOpen.has(key) ? 'Hide the math' : 'Show the math'}</button>
      ${this.mathOpen.has(key) ? `<div class="sr-math">${this.mathHtml(s)}</div>` : ''}
      ${emailPanel}
      ${overrideBox}`;
  }

  // Recomputations straight from the parsed payload — the reviewer can see
  // exactly what the rules saw, not a paraphrase.
  private mathHtml(s: any): string {
    const p = s.parsed;
    if (!p) return 'No parsed payload (PDF failed to parse) — nothing to recompute.';
    const L: string[] = [];
    const t = p.totals;
    if (t) {
      L.push(`Net Revenue totals   rents ${this.fmt2(t.rents)} · cleaning ${this.fmt2(t.cleaning ?? 0)} · gross ${this.fmt2(t.gross)} · channel fees ${this.fmt2(t.channel_fee)} · net ${this.fmt2(t.net)}${t.noncomm != null ? ` · non-comm ${this.fmt2(t.noncomm)} · total ${this.fmt2(t.total)}` : ''} · ${t.nights ?? '?'} nights`);
      const rows: any[] = p.rows || [];
      if (rows.length) {
        for (const k of ['rents', 'cleaning', 'gross', 'channel_fee', 'net', 'noncomm', 'total']) {
          if (t[k] == null) continue;
          const sum = Math.round(rows.reduce((a, r) => a + (r[k] || 0), 0) * 100) / 100;
          L.push(`  Σ rows.${k.padEnd(11)} ${this.fmt2(sum)}  vs totals ${this.fmt2(t[k])}  Δ ${this.fmt2(Math.abs(sum - t[k]))}`);
        }
      }
    } else {
      L.push('No Net Revenue table parsed (no-bookings statement).');
    }
    const r = s.rate_used;
    const commActual = Math.abs(p.commission_total || 0);
    if (r && t) {
      const base = r.formula === 'MMB' ? (t.rents || 0) : (t.net || 0);
      const baseName = r.formula === 'MMB' ? 'Rents' : 'Net Revenue';
      const expected = Math.round((Number(r.mgmt_pct) || 0) / 100 * base * 100) / 100;
      L.push(`Commission           ${this.fmt2(commActual)} charged  vs  ${r.mgmt_pct}% × ${baseName} ${this.fmt2(base)} = ${this.fmt2(expected)}  Δ ${this.fmt2(Math.abs(commActual - expected))}  (formula ${r.formula})`);
    } else if (!r) {
      L.push('Commission           rate row missing — expected value unknown.');
    }
    const st = p.section_totals || {};
    const secs = Object.entries(st).map(([k, v]) => `${k} ${this.fmt2(v as number)}`).join(' · ');
    if (secs) L.push(`Expense sections     ${secs}`);
    const sm = p.summary || {};
    if (sm.starting_balance != null && sm.net_income != null) {
      const expenseSum: number = Object.values(st as Record<string, any>).reduce((a: number, v: any) => a + (Number(v) || 0), 0);
      // R55: mirror server rule 5.10 exactly — NI base is the table's Total
      // column (net + non-commissionable fees) when present, else net.
      const niBase = (t?.total ?? t?.net) || 0;
      const niComputed = Math.round((niBase + expenseSum) * 100) / 100;
      L.push(`Summary box          start ${this.fmt2(sm.starting_balance)} + NI ${this.fmt2(sm.net_income)} = ${this.fmt2(sm.starting_balance + sm.net_income)}  vs current ${this.fmt2(sm.current_balance)}`);
      L.push(`  NI recomputed      rev total ${this.fmt2(niBase)} + sections ${this.fmt2(expenseSum)} = ${this.fmt2(niComputed)}  vs stated ${this.fmt2(sm.net_income)}  Δ ${this.fmt2(Math.abs(niComputed - sm.net_income))}`);
      L.push(`  Payout             current ${this.fmt2(sm.current_balance)} − payout ${this.fmt2(sm.owner_payout)} = ${this.fmt2((sm.current_balance ?? 0) - (sm.owner_payout ?? 0))}  vs ending ${this.fmt2(sm.ending_balance)}`);
    } else {
      L.push('Summary box          incomplete — recompute not possible (which is itself finding 5.10).');
    }
    return this.esc(L.join('\n'));
  }

  // ── rates view ──────────────────────────────────────────────
  private drawRates(): string {
    if (this.ratesLoadError) {
      // no editable table (and no Save) over a sheet that didn't load
      return `<div class="sr-card" role="alert" style="padding:16px 18px; display:flex; gap:12px; align-items:center; flex-wrap:wrap;">
        <div style="flex:1 1 260px; font-size:13px; color:var(--danger-text);">${this.esc(this.ratesLoadError)}</div>
        <button class="sr-btn" id="sr-rates-reload" style="min-height:36px;">Reload</button>
      </div>`;
    }
    const rec = (r: any, k: string) => r.recurring?.[k] ?? '';
    const rows = this.rates.map((r, i) => `
      <tr data-idx="${i}">
        <td><input type="text" class="wide" data-f="name" value="${this.esc(r.name)}" /></td>
        <td><select data-f="company">${['WCH', 'MMB', 'SS'].map(c => `<option ${r.company === c ? 'selected' : ''}>${c}</option>`).join('')}</select></td>
        <td><select data-f="formula">${['WCH', 'MMB', 'WCH-keep-cleaning', 'arbitrage', 'split-str-mtr'].map(c => `<option ${r.formula === c ? 'selected' : ''}>${c}</option>`).join('')}</select></td>
        <td><input type="number" step="0.1" data-f="mgmt_pct" value="${this.esc(r.mgmt_pct ?? '')}" /></td>
        <td><input type="number" step="0.1" data-f="mgmt_pct_mtr" value="${this.esc(r.mgmt_pct_mtr ?? '')}" /></td>
        <td><input type="number" data-f="tech" value="${this.esc(r.tech ?? '')}" /></td>
        <td><input type="number" data-f="damage_waiver" value="${this.esc(r.damage_waiver ?? '')}" /></td>
        <td style="text-align:center;"><input type="checkbox" data-f="withholding" ${r.withholding ? 'checked' : ''} /></td>
        <td><input type="number" data-f="cleaning_rate" value="${this.esc(r.cleaning_rate ?? '')}" /></td>
        ${['lawn', 'pool', 'pest', 'garbage', 'wifi', 'streaming'].map(k => `<td><input type="number" data-f="rec_${k}" value="${this.esc(rec(r, k))}" /></td>`).join('')}
        <td style="text-align:center;"><input type="checkbox" data-f="pest_every_other_month" ${r.pest_every_other_month ? 'checked' : ''} /></td>
        <td><input type="number" data-f="supplies_budget" value="${this.esc(r.supplies_budget ?? '')}" title="Monthly supplies budget — statements over it get flagged" /></td>
        <td style="text-align:center;"><input type="checkbox" data-f="owner_approval_required" ${r.owner_approval_required ? 'checked' : ''} title="EVERY supplies/maintenance line needs an owner-approval comment (e.g. 3880/3884 Bayside)" /></td>
        <td style="text-align:center;"><input type="checkbox" data-f="commission_after_expenses" ${r.commission_after_expenses ? 'checked' : ''} title="Special structure: commission taken after operating expenses (e.g. Titus)" /></td>
        <td><input type="text" data-f="off_board" value="${this.esc(typeof r.off_board === 'string' ? r.off_board : r.off_board ? 'yes' : '')}" placeholder="blank = active" /></td>
        <td><input type="text" class="wide" data-f="launch_note" value="${this.esc(r.launch_note ?? '')}" /></td>
        <td><input type="text" class="wide" data-f="note" value="${this.esc(r.note ?? '')}" /></td>
        <td><button class="sr-link" data-del="${i}" title="Remove row">✕</button></td>
      </tr>`).join('');
    return `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:10px; flex-wrap:wrap;">
        <div style="font-size:13px; font-weight:600;">Rate table — ${this.rates.length} propert${this.rates.length === 1 ? 'y' : 'ies'} on the sheet</div>
        <span class="sr-note">commission, tech fee, damage waiver, withholding and recurring services are checked against these rows on every upload</span>
        <button class="sr-btn sr-btn-ghost" id="sr-rate-add" style="margin-left:auto;">+ Add property</button>
        <button class="sr-btn" id="sr-rate-save" ${this.ratesSaving ? 'disabled' : ''}>${this.ratesSaving ? 'Saving…' : 'Save rate table'}</button>
      </div>
      ${this.ratesNote ? `<div class="sr-note" style="margin-bottom:8px;">${this.esc(this.ratesNote)}</div>` : ''}
      <div class="sr-card sr-rates-wrap" style="padding:0;">
        <table class="sr-table sr-rates">
          <thead><tr>
            <th>Property</th><th>Co.</th><th>Formula</th><th>Mgmt %</th><th>MTR %</th><th>Tech</th><th>DW</th><th>7% WH</th><th>Clean rate</th>
            <th>Lawn</th><th>Pool</th><th>Pest</th><th>Garbage</th><th>WiFi</th><th>Streaming</th><th>Pest EOM</th>
            <th title="Monthly supplies budget">Supply $/mo</th><th title="Owner-approval comment required on every line">Own. appr.</th><th title="Commission after operating expenses">Comm-after-exp</th>
            <th>Off-board</th><th>Launch note</th><th>Note</th><th></th>
          </tr></thead>
          <tbody>${rows || `<tr><td colspan="23" class="sr-empty">Rate sheet is empty — add a property or hit GET /rates once to seed the examples.</td></tr>`}</tbody>
        </table>
      </div>`;
  }

  private ratesFromDom(): any[] {
    const out: any[] = [];
    this.container.querySelectorAll('table.sr-rates tbody tr[data-idx]').forEach(tr => {
      const get = (f: string) => tr.querySelector(`[data-f="${f}"]`) as HTMLInputElement | HTMLSelectElement | null;
      const num = (f: string) => { const v = (get(f) as HTMLInputElement)?.value?.trim(); return v ? Number(v) : undefined; };
      const txt = (f: string) => (get(f) as HTMLInputElement)?.value?.trim() || '';
      const chk = (f: string) => !!(get(f) as HTMLInputElement)?.checked;
      const recurring: Record<string, number> = {};
      for (const k of ['lawn', 'pool', 'pest', 'garbage', 'wifi', 'streaming']) {
        const v = num(`rec_${k}`);
        if (v != null && v > 0) recurring[k] = v;
      }
      const offRaw = txt('off_board');
      // R57: start from the SOURCE row so fields this editor doesn't show
      // (listing_id, formula_code, reserves, custom…) survive a save — the
      // old rebuild-from-DOM silently dropped them.
      const idx = Number((tr as HTMLElement).dataset.idx);
      const base = Number.isFinite(idx) && this.rates[idx] ? { ...this.rates[idx] } : {};
      const row: any = {
        ...base,
        name: txt('name'), company: txt('company'), formula: txt('formula'),
        tech: num('tech') ?? 0, damage_waiver: num('damage_waiver') ?? 0,
        withholding: chk('withholding'), recurring,
        pest_every_other_month: chk('pest_every_other_month'),
        supplies_budget: num('supplies_budget'),
        owner_approval_required: chk('owner_approval_required'),
        commission_after_expenses: chk('commission_after_expenses'),
        off_board: offRaw === '' ? false : /^(yes|true)$/i.test(offRaw) ? true : offRaw,
        launch_note: txt('launch_note') || undefined, note: txt('note'),
      };
      // Blank Mgmt % means "not entered" — the rules engine then falls back to
      // the assigned formula's rate. Coercing blank→0 made 0% an EXPLICIT
      // manual rate and flagged every formula-driven commission as HIGH.
      const mp = num('mgmt_pct'); if (mp != null) row.mgmt_pct = mp; else delete row.mgmt_pct;
      const mtr = num('mgmt_pct_mtr'); if (mtr != null) row.mgmt_pct_mtr = mtr; else delete row.mgmt_pct_mtr;
      const cr = num('cleaning_rate'); if (cr != null) row.cleaning_rate = cr; else delete row.cleaning_rate;
      if (row.supplies_budget == null) delete row.supplies_budget;
      // never persist loadRates' DERIVED enrichments — they'd freeze a stale
      // formula rate into the shared sheet
      delete row._formula_pct; delete row._formula_basis; delete row.seeded;
      out.push(row); // keep blank-name rows so delete/add indices stay stable; filtered at save
    });
    return out;
  }

  private async saveRates(): Promise<void> {
    if (this.ratesSaving) return;
    const all = this.ratesFromDom();
    const rates = all.filter(r => r.name);
    this.ratesSaving = true;
    const btn = document.getElementById('sr-rate-save') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
    try {
      const res = await fetch('/api/statements/rates', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rates, base_version: this.ratesVersion }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        this.rates = all;   // keep the edits on screen through the redraw
        this.ratesNote = `Save failed: ${body?.error || res.status}`;
      } else {
        this.ratesVersion = body.version || res.headers.get('X-Settings-Version') || this.ratesVersion;
        this.rates = body.rates || rates;
        this.ratesNote = `Saved ${body.saved} rows — new revision at ${this.uploadedLabel(body.revised_at)}. Future uploads check against this revision; already-reviewed batches keep the verdicts they got.`;
      }
    } catch (e: any) {
      this.rates = all;
      this.ratesNote = `Save failed: ${e?.message || 'network error'} — nothing was saved; try again.`;
    } finally {
      this.ratesSaving = false;
    }
    this.draw();
  }

  // ── events ──────────────────────────────────────────────────
  private bindBody(): void {
    const body = document.getElementById('sr-body');
    if (!body) return;
    body.querySelectorAll('th[data-sort]').forEach(th => th.addEventListener('click', () => {
      const k = (th as HTMLElement).dataset.sort!;
      if (this.sortKey === k) this.sortDir = this.sortDir === 1 ? -1 : 1;
      else { this.sortKey = k; this.sortDir = 1; }
      this.rowSnapshot = null;   // R88 #1: a header click is an explicit re-sort
      this.draw();
    }));
    // R88 #1.1: live property/owner search — draw() itself restores focus and
    // caret (review #1); rAF just coalesces bursts of keystrokes per frame.
    (body.querySelector('#sr-prop-search') as HTMLInputElement | null)?.addEventListener('input', (e) => {
      this.propSearch = (e.target as HTMLInputElement).value;
      requestAnimationFrame(() => this.draw());
    });
    body.querySelectorAll('tr.sr-row').forEach(tr => tr.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('button')) return;
      const key = (tr as HTMLElement).dataset.prop!;
      if (this.expanded.has(key)) this.expanded.delete(key); else this.expanded.add(key);
      this.draw();
    }));
    body.querySelectorAll('[data-math]').forEach(b => b.addEventListener('click', () => {
      const key = (b as HTMLElement).dataset.math!;
      if (this.mathOpen.has(key)) this.mathOpen.delete(key); else this.mathOpen.add(key);
      this.draw();
    }));
    // R57: draft + copy the owner note
    body.querySelectorAll('[data-owner-email]').forEach(b => b.addEventListener('click', async () => {
      const key = (b as HTMLElement).dataset.ownerEmail!;
      if (this.ownerEmailBusy) return;
      this.ownerEmailBusy = key; this.draw();
      const r = await fetch('/api/statements/owner-email', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: this.batch?.id, property: key }),
      }).catch(() => null);
      const j = r ? await r.json().catch(() => ({})) : null;
      this.ownerEmailBusy = null;
      if (!r || !r.ok) { alert(`Couldn't draft the note — ${(j && j.error) || 'try again'}`); this.draw(); return; }
      this.ownerEmails[key] = j.email || '';
      this.draw();
    }));
    body.querySelectorAll('[data-oe-copy]').forEach(b => b.addEventListener('click', async () => {
      const key = (b as HTMLElement).dataset.oeCopy!;
      const ta = document.getElementById(`sr-oe-${key}`) as HTMLTextAreaElement | null;
      if (!ta) return;
      try { await navigator.clipboard.writeText(ta.value); (b as HTMLElement).textContent = '✓ Copied'; setTimeout(() => { (b as HTMLElement).textContent = '📋 Copy to clipboard'; }, 1500); }
      catch { ta.select(); document.execCommand('copy'); }
    }));
    body.querySelectorAll('[data-override-open]').forEach(b => b.addEventListener('click', () => {
      const key = (b as HTMLElement).dataset.overrideOpen!;
      this.expanded.add(key);
      this.draw();
      (document.getElementById(`sr-ov-note-${key}`) as HTMLInputElement)?.focus();
    }));
    // R85: per-finding review — thread toggles, draft capture (draw() rebuilds
    // the body wholesale, so text lives in frDrafts), and the four actions.
    const frKeyOf = (el: HTMLElement) => `${el.dataset.frProp || ''}\u0000${el.dataset.frId || ''}`;
    // R86: assignee filter + per-finding assignment.
    body.querySelectorAll('[data-fr-filter]').forEach(b => b.addEventListener('click', () => {
      this.assigneeFilter = (b as HTMLElement).dataset.frFilter || '';
      this.rowSnapshot = null;   // R88 #1: filter change re-snapshots the worklist
      this.draw();
    }));
    (body.querySelector('#sr-frf-person') as HTMLSelectElement | null)?.addEventListener('change', (e) => {
      this.assigneeFilter = (e.target as HTMLSelectElement).value;
      this.rowSnapshot = null;
      this.draw();
    });
    const frSend = async (property: string, findingId: string, payload: any) => {
      const fkey = `${property}\u0000${findingId}`;
      const batchId = this.batch?.id;
      if (!batchId || this.frBusy) return;
      this.frBusy = fkey;
      this.draw();
      try {
        const r = await fetch('/api/statements/finding-review', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batch_id: batchId, property, finding_id: findingId, by: this.byWho(), ...payload }),
        });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        this.frBusy = null;
        if (this.batch && this.batch.id === batchId) {
          const idx = this.batch.statements.findIndex((x: any) => x.property === property);
          if (idx >= 0) this.batch.statements[idx] = j;
        }
      } catch (e: any) {
        this.frBusy = null;
        alert(`Didn't save — ${e.message}`);
      }
      this.draw();
    };
    body.querySelectorAll('[data-fr-assign]').forEach(sel => sel.addEventListener('change', () => {
      const el = sel as HTMLSelectElement;
      if (this.frBusy) { this.draw(); return; }   // review #3: snap the select back — a silently dropped pick must not keep displaying as chosen
      const id = el.value;
      if (!id) return;
      const r = this.reviewers.find(v => v.id === id);
      if (!r) return;
      void frSend(el.dataset.frProp!, el.dataset.frId!, { action: 'assign', assignee: { id: r.id, name: r.name } });
    }));
    body.querySelectorAll('[data-fr-toggle]').forEach(b => b.addEventListener('click', () => {
      const k = frKeyOf(b as HTMLElement);
      if (this.frOpen.has(k)) this.frOpen.delete(k); else this.frOpen.add(k);
      this.draw();
    }));
    body.querySelectorAll('[data-fr-input]').forEach(el => el.addEventListener('input', () => {
      this.frDrafts.set(frKeyOf(el as HTMLElement), (el as HTMLInputElement).value);
    }));
    body.querySelectorAll('[data-fr-act]').forEach(b => b.addEventListener('click', async () => {
      const el = b as HTMLElement;
      const action = el.dataset.frAct!;
      const property = el.dataset.frProp!;
      const findingId = el.dataset.frId!;
      if (action === 'unassign') { void frSend(property, findingId, { action: 'unassign' }); return; }   // review #2: text-free — never publish or eat the thread draft
      const fkey = `${property}\u0000${findingId}`;
      if (this.frBusy) return;
      const text = (this.frDrafts.get(fkey) || '').trim();
      if (action === 'comment' && !text) {
        const inp = Array.from(body.querySelectorAll<HTMLInputElement>('[data-fr-input]'))
          .find(i => i.dataset.frProp === property && i.dataset.frId === findingId);
        inp?.focus();
        return;
      }
      const batchId = this.batch?.id;
      if (!batchId) return;
      this.frBusy = fkey;
      this.draw();
      try {
        const r = await fetch('/api/statements/finding-review', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batch_id: batchId, property, finding_id: findingId, action, text, by: this.byWho() }),
        });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        this.frBusy = null;
        this.frDrafts.delete(fkey);
        if (this.batch && this.batch.id === batchId) {
          const idx = this.batch.statements.findIndex((x: any) => x.property === property);
          if (idx >= 0) this.batch.statements[idx] = j;
        }
      } catch (e: any) {
        this.frBusy = null;
        alert(`Didn't save — ${e.message}`);
      }
      this.draw();
    }));
    // review #2: DOM-only drafts die on every redraw (live search redraws per
    // keystroke) — mirror both into state the way frDrafts does.
    body.querySelectorAll<HTMLTextAreaElement>('[data-oe-key]').forEach(ta => ta.addEventListener('input', () => {
      this.ownerEmails[ta.dataset.oeKey!] = ta.value;
    }));
    body.querySelectorAll<HTMLInputElement>('[data-ov-key]').forEach(inp => inp.addEventListener('input', () => {
      this.ovNotes.set(inp.dataset.ovKey!, inp.value);
    }));
    body.querySelectorAll('[data-override]').forEach(b => b.addEventListener('click', async () => {
      const key = (b as HTMLElement).dataset.override!;
      const note = (document.getElementById(`sr-ov-note-${key}`) as HTMLInputElement)?.value || '';
      const res = await fetch('/api/statements/override', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: this.batch.id, property: key, status: 'APPROVE', note, by: this.byWho() }),
      });
      if (res.ok) {
        const st = await res.json();
        const idx = this.batch.statements.findIndex((s: any) => s.property === key);
        if (idx >= 0) this.batch.statements[idx] = st;
        this.ovNotes.delete(key);   // submitted — the draft is spent
        this.draw();
      }
    }));
    const pick = document.getElementById('sr-batch-pick') as HTMLSelectElement;
    pick?.addEventListener('change', async () => {
      this.batch = await fetch(`/api/statements/batch/${pick.value}`).then(r => r.ok ? r.json() : null).catch(() => null);
      this.rowSnapshot = null;   // R88 #1: switching batches is a new worklist
      this.expanded.clear(); this.mathOpen.clear();
      this.draw();
    });
    const copyBtn = document.getElementById('sr-copy-slack') as HTMLButtonElement;
    copyBtn?.addEventListener('click', async () => {
      try {
        const text = await fetch(`/api/statements/slack/${this.batch.id}`).then(r => r.text());
        await navigator.clipboard.writeText(text);
        copyBtn.textContent = 'Copied ✓';
      } catch { copyBtn.textContent = 'Copy failed'; }
      setTimeout(() => { copyBtn.textContent = 'Copy Slack report'; }, 2000);
    });
    document.getElementById('sr-rate-add')?.addEventListener('click', () => {
      this.rates = this.ratesFromDom();
      this.rates.push({ name: '', company: 'WCH', formula: 'WCH', mgmt_pct: 20, tech: 0, damage_waiver: 0, withholding: false, recurring: {}, note: '' });
      this.draw();
    });
    document.getElementById('sr-rate-save')?.addEventListener('click', () => this.saveRates());
    document.getElementById('sr-rates-reload')?.addEventListener('click', async () => { await this.loadRates(); this.draw(); });
    body.querySelectorAll('[data-del]').forEach(b => b.addEventListener('click', () => {
      this.rates = this.ratesFromDom();
      this.rates.splice(Number((b as HTMLElement).dataset.del), 1);
      this.draw();
    }));
  }
}
